/**
 * Businesses List Page
 * Displays all businesses and provides CRUD operations
 */

import { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { platformAdminApi } from '@/api/client';
import { CreateBusinessDialog } from './CreateBusinessDialog';
import { DeleteBusinessDialog } from './DeleteBusinessDialog';

interface Business {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  subscriptionTier: 'solo' | 'team';
}

export function BusinessesListPage() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState<Business[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  const loadBusinesses = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await platformAdminApi.listBusinesses();
      const typed = result as Business[];
      setBusinesses(typed);
      setFilteredBusinesses(typed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBusinesses();
  }, []);

  useEffect(() => {
    const search = searchTerm.toLowerCase();
    setFilteredBusinesses(
      businesses.filter(
        (b) =>
          b.name.toLowerCase().includes(search) ||
          b.slug.toLowerCase().includes(search) ||
          b.id.toLowerCase().includes(search)
      )
    );
  }, [searchTerm, businesses]);

  const handleCreate = async (data: { name: string; slug: string; timezone: string }) => {
    try {
      await platformAdminApi.createBusiness({
        name: data.name,
        slug: data.slug,
        timezone: data.timezone,
        businessHours: [],
      });
      await loadBusinesses();
    } catch (err) {
      throw err;
    }
  };

  const handleDelete = async () => {
    if (!selectedBusiness) return;
    try {
      await platformAdminApi.deleteBusiness(selectedBusiness.id);
      await loadBusinesses();
      setDeleteDialogOpen(false);
      setSelectedBusiness(null);
    } catch (err) {
      throw err;
    }
  };

  const handleUpdateTier = async (business: Business, tier: 'solo' | 'team') => {
    try {
      await platformAdminApi.updateSubscriptionTier(business.id, tier);
      await loadBusinesses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tier');
    }
  };

  const handleCleanupOrphans = async () => {
    setCleanupLoading(true);
    setError(null);
    try {
      await platformAdminApi.cleanupOrphanMemberships();
      // Optionally reload businesses
      await loadBusinesses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cleanup orphans');
    } finally {
      setCleanupLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4">Businesses</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleCleanupOrphans}
              disabled={cleanupLoading}
            >
              {cleanupLoading ? 'Cleaning...' : 'Cleanup Orphans'}
            </Button>
            <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
              Create Business
            </Button>
            <Tooltip title="Refresh">
              <IconButton onClick={loadBusinesses} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label="Search"
          placeholder="Search by name, slug, or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#F6F7F5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Slug</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Timezone</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Subscription Tier</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBusinesses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">No businesses found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBusinesses.map((business) => (
                  <TableRow key={business.id}>
                    <TableCell>{business.name}</TableCell>
                    <TableCell>{business.slug}</TableCell>
                    <TableCell>{business.timezone}</TableCell>
                    <TableCell>
                      <Select
                        value={business.subscriptionTier}
                        onChange={(e) =>
                          handleUpdateTier(business, e.target.value as 'solo' | 'team')
                        }
                        size="small"
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value="solo">Solo</MenuItem>
                        <MenuItem value="team">Team</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedBusiness(business);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <CreateBusinessDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={handleCreate}
      />

      {selectedBusiness && (
        <DeleteBusinessDialog
          open={deleteDialogOpen}
          businessName={selectedBusiness.name}
          businessSlug={selectedBusiness.slug}
          onClose={() => {
            setDeleteDialogOpen(false);
            setSelectedBusiness(null);
          }}
          onDelete={handleDelete}
        />
      )}
    </Container>
  );
}
